import {
  Button,
  DialogClose,
  DialogFooter,
  DialogHeader,
  DialogSection,
  Label,
  MultiSelect,
  MultiSelectOption,
  NumberInput,
} from "@tellescope/ui";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "@tellescope/forms";
import { useAuth, useTellescopeInfiniteQuery, useTellescopeMutation, useUsersQuery } from "@tellescope/tanstack-query";
import { formatName } from "@tellescope/utils";
import { WeeklyAvailability } from "@tellescope/types-models";
import { startOfDay } from "date-fns/startOfDay";
import { endOfDay } from "date-fns/endOfDay";
import type { EventFormInitialValues } from "../../../../hooks";
import { CreateEventHeaderButton } from "./CreateEventHeaderButton";
import { IntervalSelector } from "../../../AvailabilityView/IntervalSelector";

const PRE_BUFFER_INTERVALS = [5, 10, 15, 20, 30];
const DEFAULT_DURATION_MINUTES = 60;
const MINUTES_PER_DAY = 24 * 60;

export interface AvailabilityFormDialogContentsProps {
  onSuccess: () => void;
  onCancel: () => void;
  timezone?: string;
  initialFormValues?: EventFormInitialValues;
  onEventTabClick: () => void;
}

export const AvailabilityFormDialogContents = ({
  onSuccess,
  onCancel,
  initialFormValues,
  onEventTabClick,
}: AvailabilityFormDialogContentsProps) => {
  const { t } = useTranslation("calendar");
  const { currentUser, isAdmin } = useAuth();

  const { data: allUsers = [] } = useUsersQuery();
  // Non-admins can only add availability for themselves; admins can add it for anyone.
  const selectableUsers = useMemo(
    () => (isAdmin ? allUsers : allUsers.filter((user) => user.id === currentUser.id)),
    [allUsers, isAdmin, currentUser.id]
  );
  const userOptions = useMemo<MultiSelectOption[]>(
    () =>
      selectableUsers.map((user) => ({
        value: user.id,
        label: formatName(user) || t("User: {{id}}", { id: user.id }),
      })),
    [selectableUsers, t]
  );

  const templatesQuery = useTellescopeInfiniteQuery({
    model: "calendar_event_templates",
    method: "getSome",
    request: {},
    fetchAllPages: true,
    select: (data) => data.pages.flat(),
  });
  const templateOptions = useMemo<MultiSelectOption[]>(
    () =>
      (templatesQuery.data ?? []).map((template) => ({
        value: template.id,
        label: template.displayTitle || template.title,
      })),
    [templatesQuery.data]
  );

  const updateUser = useTellescopeMutation({ model: "users", method: "updateOne" });

  const selectableUserIds = useMemo(() => new Set(selectableUsers.map((user) => user.id)), [selectableUsers]);
  const initialUserIds = useMemo(() => {
    if (!isAdmin) return [currentUser.id];
    return initialFormValues?.userIds?.filter((id) => selectableUserIds.has(id)) ?? [];
  }, [isAdmin, currentUser.id, initialFormValues?.userIds, selectableUserIds]);

  const form = useForm({
    fields: {
      userIds: { type: "multiselect", initialValue: initialUserIds },
      priority: { type: "number", initialValue: 0 },
      // 0 is the "Default" / "None" (unset) state for these; mapped to undefined on submit.
      intervalInMinutes: { type: "number", initialValue: 0 },
      bufferStartMinutes: { type: "number", initialValue: 0 },
      validTemplateIds: { type: "multiselect", initialValue: [] },
    },
    onSubmit: async (values) => {
      const startDate = initialFormValues?.startDate;
      const targetUserIds = values.userIds.filter((id) => selectableUserIds.has(id));
      if (!startDate || !targetUserIds.length) return;

      const startTimeInMinutes = startDate.getHours() * 60 + startDate.getMinutes();
      // Clamp to end of the start day so a click near midnight (or any drag) never spans into the next day.
      const endTimeInMinutes = Math.min(
        startTimeInMinutes + (initialFormValues?.duration ?? DEFAULT_DURATION_MINUTES),
        MINUTES_PER_DAY
      );
      const block: WeeklyAvailability = {
        dayOfWeekStartingSundayIndexedByZero: startDate.getDay(),
        startTimeInMinutes,
        endTimeInMinutes,
        // Limit the block to the specific day the click/drag happened.
        active: { from: startOfDay(startDate), to: endOfDay(startDate) },
        priority: values.priority || undefined,
        intervalInMinutes: values.intervalInMinutes || undefined,
        bufferStartMinutes: values.bufferStartMinutes || undefined,
        validTemplateIds: values.validTemplateIds.length ? values.validTemplateIds : undefined,
      };

      await Promise.allSettled(
        targetUserIds.map((id) =>
          updateUser.mutateAsync({
            id,
            weeklyAvailabilities: [block],
          })
        )
      );
      onSuccess();
    },
  });

  return (
    <>
      <DialogHeader className="font-semibold p-4 text-lg">
        <span className="flex gap-2 items-center">
          <CreateEventHeaderButton onClick={onEventTabClick}>{t("Create Event")}</CreateEventHeaderButton>
          <span className="text-muted-foreground">{"•"}</span>
          {t("Availability")}
        </span>
      </DialogHeader>
      <DialogSection className="p-4 space-y-4 overflow-auto">
        <div className="space-y-1">
          <Label className="text-muted-foreground text-xs font-light">{t("Users")}</Label>
          <MultiSelect
            {...form.getCustomFieldProps("userIds")}
            options={userOptions}
            placeholder={t("Search users...")}
            className="font-light w-full"
          />
        </div>
        <div className="flex gap-2 items-start w-full">
          <div className="space-y-1 flex-1">
            <Label className="text-muted-foreground text-xs font-light">{t("Priority")}</Label>
            <NumberInput
              value={form.values.priority || undefined}
              step={1}
              max={10}
              min={0}
              className="text-xs md:text-xs h-auto py-1 w-full"
              onChange={(priority) => form.setFieldValue("priority", priority ?? 0)}
              placeholder={t("Default")}
            />
          </div>
          <IntervalSelector
            label={t("Slot interval (min)")}
            className="flex-1"
            value={form.values.intervalInMinutes || undefined}
            onChange={(intervalInMinutes) => form.setFieldValue("intervalInMinutes", intervalInMinutes ?? 0)}
          />
          <IntervalSelector
            label={t("Pre-buffer (min)")}
            defaultValue={t("None")}
            className="flex-1"
            value={form.values.bufferStartMinutes || undefined}
            onChange={(bufferStartMinutes) => form.setFieldValue("bufferStartMinutes", bufferStartMinutes ?? 0)}
            intervals={PRE_BUFFER_INTERVALS}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-muted-foreground text-xs font-light">{t("Event types (blank = all)")}</Label>
          <MultiSelect
            {...form.getCustomFieldProps("validTemplateIds")}
            options={templateOptions}
            isLoading={templatesQuery.isLoading}
            placeholder={t("Search event types...")}
            className="font-light w-full"
          />
        </div>
      </DialogSection>
      <DialogFooter className="bg-gray-100">
        <DialogClose asChild>
          <Button variant="outline" onClick={onCancel}>
            {t("Cancel")}
          </Button>
        </DialogClose>
        <Button
          onClick={form.submitForm}
          loading={form.isSubmitting}
          disabled={!form.values.userIds.length || !initialFormValues?.startDate}
        >
          {t("Create Availability")}
        </Button>
      </DialogFooter>
    </>
  );
};
